import {
  LOAD_TARIFICATION_SETTINGS,
  LOGIN_EMR_NO_LICENSE,
  LOGIN_EMR_SERVER,
  LOGIN_EMR_SERVER_ERROR,
  LOGIN_EMR_SERVER_FETCHING,
  LOGIN_EMR_SERVER_SUCCESS,
  LOGIN_EMR_SERVER_TENANT_OPTIONS,
  LOGIN_PASSPORT_CALLBACK,
  LOGIN_STATUS_ERROR,
  LOGIN_STATUS_LOGGING_IN,
  LOGIN_STATUS_NO_LICENCE,
  LOGIN_STATUS_PARSING_PASSPORT,
  LOGIN_STATUS_RETRIEVING,
  LOGIN_STATUS_SUCCESS,
  LOGIN_STATUS_TENANT_OPTIONS,
  LOGIN_STS_RECEIVED,
  LoginSuccess,
  NoLicense,
  SET_USER_CONFIGURATION,
  UserContextActionTypes,
  UserContextLoginStatus,
} from "store/types/user-context.types";
import { GlobalState } from "store/configure-store";
import { TenantOption } from "common/dto/TenantOption";
import { UserContext } from "common/models/UserContext";
import { User, UserType } from "model/user.model";
import { UserConfiguration } from "model/user-configuration.model";

export interface UserContextState {
  user: UserType;
  practice: string;
  ehealthKeyStorePasswordKey: string;
  stsToken: string;
  loginError: string;
  licenseUrl: string;
  loginStatus: UserContextLoginStatus;
  tenantOptions: Array<TenantOption>;
  useEAttest: boolean;
  userConfiguration: UserConfiguration;
}

export const initialUserContextState: UserContextState = new UserContext();

/** ---- SELECTORS ---- */
export const selectStsToken = (state: GlobalState): string => {
  return state.userContext.stsToken;
};

export const selectUser = (state: GlobalState): User => {
  return new User(state.userContext.user);
};

export const selectPractice = (state: GlobalState): string => {
  return state.userContext.practice;
};

export const selectLoginStatus = (
  state: GlobalState
): UserContextLoginStatus => {
  return state.userContext.loginStatus;
};

export const selectLoginError = (state: GlobalState): string => {
  return state.userContext.loginError;
};

export const selectTenantOptions = (
  state: GlobalState
): Array<TenantOption> => {
  return state.userContext.tenantOptions;
};

export const selectLicenseUrl = (state: GlobalState): string => {
  return state.userContext.licenseUrl;
};

export const selectEAttestAvailable = (state: GlobalState): boolean => {
  return state.userContext.useEAttest;
};

export const selectUserConfiguration = (
  state: GlobalState
): UserConfiguration => {
  return state.userContext.userConfiguration;
};

/** ---- ACTION HANDLERS ---- */
const loginStsReceived = (state: UserContextState, token: string) => {
  return { ...state, stsToken: token };
};

const loginPassportCallback = (state: UserContextState) => {
  return {
    ...state,
    loginStatus: LOGIN_STATUS_PARSING_PASSPORT as UserContextLoginStatus,
    loginError: "",
    tenantOptions: [],
  };
};

const loginEmrServerFetching = (state: UserContextState) => {
  return {
    ...state,
    loginStatus: LOGIN_STATUS_RETRIEVING as UserContextLoginStatus,
    tenantOptions: [],
  };
};

const loginEmrServer = (state: UserContextState) => {
  return {
    ...state,
    loginStatus: LOGIN_STATUS_LOGGING_IN as UserContextLoginStatus,
  };
};

const loginEmrServerTenantOptions = (
  state: UserContextState,
  options: Array<TenantOption>
) => {
  return {
    ...state,
    loginStatus: LOGIN_STATUS_TENANT_OPTIONS as UserContextLoginStatus,
    loginError: "",
    tenantOptions: options,
  };
};

const loginEmrServerError = (state: UserContextState, error: string) => {
  return {
    ...state,
    loginStatus: LOGIN_STATUS_ERROR as UserContextLoginStatus,
    loginError: error,
  };
};

const loginEmrServerSuccess = (
  state: UserContextState,
  payload: LoginSuccess
) => {
  const { practice, userConfiguration, user } = payload;
  return {
    ...state,
    practice: practice,
    ehealthKeyStorePasswordKey: userConfiguration.ehealthKeyStorePasswordKey,
    user: user,
    loginStatus: LOGIN_STATUS_SUCCESS as UserContextLoginStatus,
    loginError: "",
    tenantOptions: [],
    userConfiguration: userConfiguration,
  };
};

const loginEmrNoLicense = (state: UserContextState, payload: NoLicense) => {
  const { loginError, licenseUrl } = payload;

  return {
    ...state,
    loginStatus: LOGIN_STATUS_NO_LICENCE as UserContextLoginStatus,
    loginError: loginError,
    licenseUrl: licenseUrl,
  };
};

const loadTarificationUserSettings = (
  state: UserContextState,
  payload: boolean
) => {
  return { ...state, useEAttest: payload };
};

const setUserConfiguration = (
  state: UserContextState,
  payload: UserConfiguration
) => {
  return {
    ...state,
    userConfiguration: payload,
  };
};

/** ---- REDUCER ---- */
export const userContextReducer = (
  state = initialUserContextState,
  action: UserContextActionTypes
): UserContextState => {
  switch (action.type) {
    case LOGIN_STS_RECEIVED:
      return loginStsReceived(state, action.payload);
    case LOGIN_PASSPORT_CALLBACK:
      return loginPassportCallback(state);
    case LOGIN_EMR_SERVER_FETCHING:
      return loginEmrServerFetching(state);
    case LOGIN_EMR_SERVER:
      return loginEmrServer(state);
    case LOGIN_EMR_SERVER_TENANT_OPTIONS:
      return loginEmrServerTenantOptions(state, action.payload);
    case LOGIN_EMR_SERVER_ERROR:
      return loginEmrServerError(state, action.payload);
    case LOGIN_EMR_SERVER_SUCCESS:
      return loginEmrServerSuccess(state, action.payload);
    case LOGIN_EMR_NO_LICENSE:
      return loginEmrNoLicense(state, action.payload);
    case LOAD_TARIFICATION_SETTINGS:
      return loadTarificationUserSettings(state, action.payload);
    case SET_USER_CONFIGURATION:
      return setUserConfiguration(state, action.payload);
    default:
      return state;
  }
};
